import type { Key } from "react-aria-components";

import React from "react";
import { Label } from "react-aria-components";

import { Button } from "@/components/button";
import {
  ComboBox,
  ComboBoxButton,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxListBox,
  ComboBoxListItem,
  ComboBoxPopover,
} from "@/components/combobox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import { SelectListItemLabel } from "@/components/select";
import { useUpdateAccountModal } from "@/features/request/shared/UseUpdateAccountModal";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";
import { useListProjectsMinimal } from "@/hooks/useListProjectsMinimal.ts";

interface ChangeAccountModalProps {
  isOpen: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
}

export function ChangeAccountModal({
  isOpen,
  onOpenChange,
}: ChangeAccountModalProps) {
  const { data: request } = useGetRequest();
  const requestId = request?.id;

  const [selectedAccountId, setSelectedAccountId] = React.useState<Key | null>(
    null,
  );

  const updateAccountMutation = useUpdateAccountModal();

  const handleChangeAccount = async () => {
    if (!selectedAccountId) return;
    try {
      await updateAccountMutation.mutateAsync({
        projectId: Number(selectedAccountId),
        requestId: Number(requestId),
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating account:", error);
    }
  };

  return (
    <Modal
      classNames={{ modal: "w-[400px]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog>
        <DialogBody>
          <div className="flex flex-col gap-xs">
            <span className="text-lg font-semibold text-text-primary">
              Change Account
            </span>
            <DialogCloseButton />
          </div>
          <AccountSelector
            selectedAccountId={selectedAccountId}
            setSelectedAccountId={setSelectedAccountId}
          />
        </DialogBody>
        <DialogFooter className="flex flex-grow-1 mt-lg">
          <DialogCloseButton>Cancel</DialogCloseButton>
          <Button
            className="flex-1"
            color="gray"
            onPress={() => onOpenChange(false)}
            size="lg"
            variant="secondary"
          >
            Nevermind
          </Button>
          <Button
            className="flex-1"
            color="destructive"
            isDisabled={!selectedAccountId}
            isLoading={updateAccountMutation.isPending}
            onPress={handleChangeAccount}
            size="lg"
            variant="primary"
          >
            Change
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

interface AccountSelectorProps {
  selectedAccountId: Key | null;
  setSelectedAccountId: React.Dispatch<React.SetStateAction<Key | null>>;
}

function AccountSelector({
  selectedAccountId,
  setSelectedAccountId,
}: AccountSelectorProps) {
  const { data, isLoading } = useListProjectsMinimal();
  const projects = data?.projects;

  return (
    <ComboBox
      allowsEmptyCollection
      isDisabled={isLoading}
      menuTrigger="input"
      onSelectionChange={setSelectedAccountId}
      selectedKey={selectedAccountId}
    >
      <Label>Select the account you like to transfer this request to:</Label>
      <ComboBoxGroup>
        <ComboBoxInput placeholder="Select account" />
        <ComboBoxButton />
      </ComboBoxGroup>

      <ComboBoxPopover>
        <ComboBoxListBox
          className="max-h-[291px]"
          items={projects}
          renderEmptyState={() => (
            <span className="p-2">No accounts found</span>
          )}
        >
          {isLoading ? (
            <ComboBoxListItem id="loading">
              Loading accounts...
            </ComboBoxListItem>
          ) : projects && projects.length > 0 ? (
            projects.map((account) => (
              <ComboBoxListItem
                id={account.id}
                key={account.name}
                textValue={account.name}
              >
                <SelectListItemLabel>{account.name}</SelectListItemLabel>
              </ComboBoxListItem>
            ))
          ) : (
            <ComboBoxListItem id="no-accounts">
              No accounts found
            </ComboBoxListItem>
          )}
        </ComboBoxListBox>
      </ComboBoxPopover>
    </ComboBox>
  );
}
